import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CurrencyEur, Plus, Trash, CheckCircle } from "@phosphor-icons/react";

/**
 * PricingPlansPanel — Mandant konfiguriert seine Abo-Preise (Name, Anzahl Leerungen, Preis).
 * Speichert unter tenant.plans[].
 * Props: tenant, onSaved(updatedTenant)
 */
const EMPTY = () => ({ plan_id: `plan_${Date.now().toString(36)}`, name: "", description: "", cleanings_per_year: 6, price_per_cleaning_cents: 1490, frequency: "monthly", active: true });

export default function PricingPlansPanel({ tenant, onSaved }) {
  const [plans, setPlans] = useState([]);
  const [contactText, setContactText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setPlans(tenant?.plans || []);
    setContactText(tenant?.contact_button_text || "");
  }, [tenant]);

  const update = (idx, patch) => {
    const next = plans.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    setPlans(next);
  };
  const remove = (idx) => setPlans(plans.filter((_, i) => i !== idx));
  const add = () => setPlans([...plans, EMPTY()]);

  const save = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      // Validate
      for (const p of plans) {
        if (!p.name) throw new Error("Jeder Plan braucht einen Namen");
        if (!p.price_per_cleaning_cents || p.price_per_cleaning_cents < 0) throw new Error(`Preis für "${p.name}" ungültig`);
      }
      const r = await api.patch(`/tenants/${tenant.tenant_id}`, { plans, contact_button_text: contactText || null });
      onSaved?.(r.data);
      setMsg("Preise gespeichert");
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;

  return (
    <div className="bg-white rounded-2xl border border-black/5 p-6 max-w-4xl" data-testid="pricing-plans-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CurrencyEur size={20} weight="bold" style={{ color: "var(--tonneva-primary)" }} />
          <h3 className="font-heading text-xl font-bold tracking-tight">Preispolitik</h3>
        </div>
        <Button size="sm" onClick={add} className="rounded-md text-white" style={{ background: "var(--tonneva-gradient)" }} data-testid="pricing-add">
          <Plus size={14} weight="bold" className="mr-1" /> Neuer Plan
        </Button>
      </div>
      <p className="text-sm text-zinc-500 mb-5">Definieren Sie Ihre Abo-Tarife — Name, Beschreibung, Häufigkeit und Preis pro Reinigung. Endkunden sehen diese Pläne im Buchungsflow.</p>

      {plans.length === 0 && (
        <div className="py-10 text-center text-zinc-400 text-sm border border-dashed border-black/10 rounded-lg mb-4">
          Noch keine Preispläne — <button onClick={add} className="underline text-[var(--tonneva-primary)]">jetzt anlegen</button>.
        </div>
      )}

      <div className="space-y-4">
        {plans.map((p, idx) => (
          <div key={p.plan_id || idx} className="grid md:grid-cols-12 gap-3 p-4 border border-black/10 rounded-lg" data-testid={`pricing-plan-${idx}`}>
            <div className="md:col-span-3">
              <Label className="text-xs">Name</Label>
              <Input className="h-10 rounded-md" value={p.name} onChange={(e) => update(idx, { name: e.target.value })} placeholder="z. B. Standard" data-testid={`pricing-name-${idx}`} />
            </div>
            <div className="md:col-span-4">
              <Label className="text-xs">Beschreibung</Label>
              <Input className="h-10 rounded-md" value={p.description || ""} onChange={(e) => update(idx, { description: e.target.value })} placeholder="Kurzbeschreibung" data-testid={`pricing-desc-${idx}`} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Leerungen / Jahr</Label>
              <Input type="number" min="1" max="52" className="h-10 rounded-md" value={p.cleanings_per_year} onChange={(e) => update(idx, { cleanings_per_year: parseInt(e.target.value) || 0 })} data-testid={`pricing-cleanings-${idx}`} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Preis pro Reinigung (€)</Label>
              <Input
                type="number" step="0.01" min="0"
                className="h-10 rounded-md"
                value={(p.price_per_cleaning_cents / 100).toFixed(2)}
                onChange={(e) => update(idx, { price_per_cleaning_cents: Math.round(parseFloat(e.target.value || 0) * 100) })}
                data-testid={`pricing-price-${idx}`}
              />
              <p className="text-[10px] text-zinc-400 mt-1">{eur(p.price_per_cleaning_cents)} pro Reinigung</p>
            </div>
            <div className="md:col-span-1 flex items-end justify-end">
              <Button variant="ghost" size="sm" className="h-9 text-red-600 hover:bg-red-50 rounded-md" onClick={() => remove(idx)} data-testid={`pricing-remove-${idx}`}>
                <Trash size={14} weight="bold" />
              </Button>
            </div>
            <div className="md:col-span-12 flex items-center gap-4 pt-1">
              <label className="flex items-center gap-2 text-xs text-zinc-600">
                <input type="checkbox" checked={p.active !== false} onChange={(e) => update(idx, { active: e.target.checked })} data-testid={`pricing-active-${idx}`} />
                Aktiv (im Buchungsflow sichtbar)
              </label>
              <span className="text-xs text-zinc-400">Jahresumsatz: <span className="font-mono">{eur(p.price_per_cleaning_cents * (p.cleanings_per_year || 0))}</span></span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 pt-6 border-t border-black/5">
        <Label className="text-xs">Kontakt-Button-Text auf der Landingpage</Label>
        <Input
          className="h-10 rounded-md max-w-lg"
          value={contactText}
          onChange={(e) => setContactText(e.target.value)}
          placeholder="z. B. Jetzt anrufen: +49 7621 555 0100"
          data-testid="pricing-contact-text"
        />
        <p className="text-[10px] text-zinc-400 mt-1">Wird als sekundärer CTA im Portal angezeigt</p>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Button onClick={save} disabled={busy} className="rounded-md text-white px-6" style={{ background: "var(--tonneva-gradient)" }} data-testid="pricing-save">
          {busy ? "…" : "Speichern"}
        </Button>
        {msg && <span className="text-sm text-emerald-700 flex items-center gap-1" data-testid="pricing-msg"><CheckCircle size={14} weight="bold" /> {msg}</span>}
        {err && <span className="text-sm text-red-600" data-testid="pricing-error">{err}</span>}
      </div>
    </div>
  );
}
